import { type Component, createMemo, For, Show, type VoidComponent } from "solid-js";
import { A, type RouteSectionProps } from "@solidjs/router";
import withPagePermissionCheck from "../../../utils/withPagePermissionCheck";
import PermissionService from "../../../services/PermissionService";
import Text from "../../../components/text/Text";
import { useCustomerState } from "../../../state/customer";
import { useTranslation } from "../../../state/i18n";
import UserService from "../../../services/UserService";
import { TextAndIconButton } from "../../../components/button/Button";
import { IconButtonMenu } from "../../../components/listItemMenu/ListItemMenu";
import { resolveName } from "../../../utils/groupUtil";
import { isEmpty } from "lodash-es";
import Pill from "../../../components/pill/Pill";
import { getCustomerRole, getRoleName } from "../../../utils/roleUtil";
import EditGroupModal from "./EditGroupModal";
import { useModalManager } from "../../../components/modal/ModalManager";
import InlineMessage from "../../../components/inlineMessage/InlineMessage";
import { MAX_GROUPS } from "../../../constants";
import DeleteGroupConfirmationModal from "./DeleteGroupConfirmationModal";
import CreateGroupModal from "./CreateGroupModal";
import { Icon } from "../../../components/icon/Icon";
import EmptyState from "../../../components/emptyState/EmptyState";
import { AddUsersToGroupModal } from "./AddUsersToGroupModal";
import { GroupAvatar } from "../../../components/avatar/Avatar.tsx";

const GroupsPage: Component<RouteSectionProps<never>> = () => {
  const { t } = useTranslation();
  const customer = useCustomerState();
  const { open } = useModalManager();

  const groupsState = UserService.getGroups(customer.customerId);
  const rolesState = UserService.getRoles(customer.customerId);

  const hasReachedGroupLimit = createMemo(() =>
    groupsState.status === "success" ? groupsState.data.length >= MAX_GROUPS : false,
  );

  return (
    <div class="flex h-full flex-col gap-6">
      <div class="flex justify-between pb-3">
        <Text text={t(($) => $.GROUPS_PAGE_TITLE)} as="h1" class="text-header-xl text-primary" />

        <Show when={!isEmpty(groupsState.data) && !hasReachedGroupLimit()}>
          <TextAndIconButton
            icon="plus"
            text={t(($) => $.GROUPS_PAGE_CREATE_GROUP)}
            variant="light"
            size="large"
            onClick={() => {
              open({
                content: () => <CreateGroupModal />,
              });
            }}
          />
        </Show>
      </div>

      <div class="relative h-full min-h-0" aria-busy={groupsState.isFetching}>
        <Show
          when={groupsState.data}
          fallback={
            <div class="absolute flex size-full flex-col items-center justify-center bg-surface-default opacity-75">
              <Icon name="spinner" size="2xl" type="solid" class="animate-spin icon-secondary" />
              <span class="text-medium-regular text-primary">{t(($) => $.GROUPS_PAGE_LOADING_TITLE)}</span>
            </div>
          }
        >
          {(data) => (
            <div class="flex h-full flex-col gap-4">
              <Show when={hasReachedGroupLimit()}>
                <InlineMessage variant="warning" label={t(($) => $.GROUPS_PAGE_LIMIT_REACHED, { limit: MAX_GROUPS })} />
              </Show>

              <Show
                when={!isEmpty(data())}
                fallback={
                  <NoGroups
                    actionOnClick={() =>
                      open({
                        content: () => <CreateGroupModal />,
                      })
                    }
                  />
                }
              >
                <div
                  role="table"
                  style={{
                    "--column-template": "calc(46% - 2.625rem) 1fr 1fr 2.5rem",
                  }}
                  class="flex min-h-0 flex-1 flex-col"
                >
                  <div role="rowgroup">
                    <div
                      role="row"
                      class="grid grid-cols-template gap-3 border-b border-subtle text-base-semibold text-secondary"
                    >
                      <div role="columnheader" class="px-3 py-1.5">
                        {t(($) => $.GROUPS_PAGE_NAME_COLUMN_TITLE)}
                      </div>
                      <div role="columnheader" class="py-1.5">
                        {t(($) => $.GROUPS_PAGE_MEMBERS_COLUMN_TITLE)}
                      </div>
                      <div role="columnheader" class="py-1.5">
                        {t(($) => $.GROUPS_PAGE_ROLE_COLUMN_TITLE)}
                      </div>
                      <div role="columnheader" class="w-10" />
                    </div>
                  </div>
                  <div role="rowgroup" class="min-h-0 flex-1 overflow-y-auto pb-6">
                    <For each={groupsState.data ?? []}>
                      {(group) => {
                        const groupMembersState = UserService.getPaginatedUsers(
                          customer.customerId,
                          // We only care for the total number of members, not the actual members, therefor a size of '0'
                          () => ({ page: 0, size: 0 }),
                          group.id,
                        );

                        return (
                          <div class="relative">
                            <A
                              href={group.id.toString()}
                              class="absolute inset-0 rounded-sm -outline-offset-1 hover:bg-surface-hover focus-visible:outline-default"
                            />
                            <div
                              role="row"
                              class="pointer-events-none relative grid grid-cols-template items-center gap-3 text-base-regular text-secondary"
                            >
                              <div role="cell" class="flex min-w-0 items-center gap-2.5 px-3 py-4">
                                <GroupAvatar group={group} size="md" />
                                <div class="flex min-w-0 items-center gap-2">
                                  <Text
                                    text={resolveName(group)}
                                    as="span"
                                    class="text-base-medium text-primary"
                                    truncate
                                  />
                                  <Show when={group.externallyManaged}>
                                    <Pill variant="informative" label={t(($) => $.USERS_PAGE_SSO_MANAGED)} />
                                  </Show>
                                </div>
                              </div>
                              <div role="cell" class="flex min-w-0 flex-wrap gap-1 py-4">
                                <Show when={groupMembersState.data}>
                                  {(groupMembers) =>
                                    t(($) => $.GROUPS_AMOUNT_OF_MEMBERS, { count: groupMembers().totalElements })
                                  }
                                </Show>
                              </div>
                              <div role="cell" class="flex min-w-0 flex-wrap gap-1 py-4">
                                <Show
                                  when={getCustomerRole(group.grants, customer.customerId, rolesState.data ?? [])}
                                  fallback={t(($) => $.GROUPS_PAGE_NO_ROLES)}
                                >
                                  {(role) => <Pill label={getRoleName(role(), t)} variant="neutral" />}
                                </Show>
                              </div>
                              <Show when={!group.externallyManaged}>
                                <div role="cell" class="pointer-events-auto">
                                  <IconButtonMenu
                                    items={[
                                      [
                                        {
                                          value: "edit",
                                          type: "action",
                                          onClick: () =>
                                            open({
                                              content: () => <EditGroupModal group={group} />,
                                            }),
                                          variant: "icon",
                                          label: t(($) => $.GROUPS_PAGE_EDIT),
                                          icon: { name: "pencil", type: "outline" },
                                        },
                                        {
                                          value: "add_users",
                                          type: "action",
                                          onClick: () =>
                                            open({
                                              content: () => <AddUsersToGroupModal group={group} />,
                                            }),
                                          variant: "icon",
                                          label: t(($) => $.GROUPS_PAGE_ADD_USERS),
                                          icon: { name: "userGroup", type: "outline" },
                                        },
                                        {
                                          value: "delete",
                                          type: "action",
                                          onClick: () =>
                                            open({
                                              content: () => (
                                                <DeleteGroupConfirmationModal
                                                  customerId={customer.customerId}
                                                  group={group}
                                                />
                                              ),
                                            }),
                                          variant: "icon",
                                          label: t(($) => $.DELETE_BUTTON),
                                          icon: { name: "trash", type: "outline" },
                                        },
                                      ],
                                    ]}
                                    aria-label={t(($) => $.ACTIONS_ARIA)}
                                  />
                                </div>
                              </Show>
                            </div>
                          </div>
                        );
                      }}
                    </For>
                  </div>
                </div>
              </Show>
            </div>
          )}
        </Show>
      </div>
    </div>
  );
};

type NoGroupsProps = {
  actionOnClick: () => void;
};
const NoGroups: VoidComponent<NoGroupsProps> = (props) => {
  const { t } = useTranslation();

  return (
    <EmptyState
      title={t(($) => $.GROUPS_PAGE_EMPTY_STATE_TITLE)}
      description={t(($) => $.GROUPS_PAGE_EMPTY_STATE_DESCRIPTION)}
      illustration={{ primaryIcon: "userGroup" }}
      action={{
        variant: "light",
        icon: "plus",
        text: t(($) => $.GROUPS_PAGE_EMPTY_STATE_ACTION_LABEL),
        onClick: () => {
          props.actionOnClick();
        },
      }}
    />
  );
};

const ProtectedGroupsPage = withPagePermissionCheck(GroupsPage, PermissionService.canManagePermissions);

export default ProtectedGroupsPage;
